"use client";
// web tapioca
// app/components/Chatbot.tsx
//
// Hallmark · component: chat widget · genre: modern-minimal · theme: design.md (locked)
// states: default · hover · focus-visible · active · disabled · loading · error · success
//
// Docked chat rail, mounted globally in app/[locale]/layout.tsx. From md up the
// panel is a full-height right rail and the page squeezes to make room; below md
// it stays a full-screen sheet (a 380px rail leaves nothing for the content).
// Replies come from ./chatbotReply, which posts the transcript to /api/chat.
// Deliberately carries no data-aos attributes: AOS's MutationObserver only
// refreshes when an added node has dataset.aos, so appending messages never
// recalculates the ~200 animated elements elsewhere on the page.
//
// No MUI: every rule lives in ./Chatbot.module.css, so the breakpoint and the
// reduced-motion check are media queries instead of useMediaQuery subscriptions,
// and the colours come from design.md's tokens rather than the theme palette
// (same hex values either way).

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import type { ChangeEvent, CSSProperties, FormEvent, ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";

import {
  CONSENT_REQUIRED,
  getChatReply,
  safeSources,
  type ChatMsg,
} from "@/app/components/chatbotReply";
import s from "./Chatbot.module.css";

const MAX_MESSAGES = 30;
const MAX_INPUT = 500;
const CLOSE_MS = 300; // must match the chat-out animation in the stylesheet

// The UI hint only: it decides whether a returning visitor sees the gate. The
// enforcement is the httpOnly ppc_chat_consent cookie /api/chat/lead sets, which
// this code cannot read — so the two can disagree, and the 403 below is how that
// disagreement resolves. Not per-locale: within one domain, switching language
// must not ask twice. Across the two domains it does — localStorage and a
// host-only cookie are both per-origin, so a visitor to both consents twice and
// writes two rows.
const GATE_KEY = "ppc_chat_gate_v1";

// The model answers in light Markdown: the system prompt asks it for short
// bullet lists, article citations and the packaging photos it may show
// (lib/chat/context.ts), and it reaches for tables on its own. Render exactly
// that much — bold, bullets, tables, images, links — and leave everything else
// as text.
//
// Built from React nodes, never dangerouslySetInnerHTML, and the href is taken
// from a capture group that can only match http(s), so no model output can
// become an anchor the browser will execute. Images work the same way: every
// ![alt](src) is consumed, and only the img1-4.png at the end of src is read —
// the src next/image gets is rebuilt in code, so a model that mistypes the
// 70-character path still shows the photo, and anything else (an unknown
// path, an http URL) renders nothing rather than raw markdown or a broken box.
// The image alternative sits before the link one, or "[alt](src)" would match
// first.
const INLINE =
  /\*\*([^*]+)\*\*|!\[([^\]]*)\]\(([^)\s]*)\)|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>"']+)/g;

// A GFM table's |---|:--:| row: dropped, and it marks the row above as the head.
// Two dashes minimum, so a body row of N/A cells ("| - | - |") stays a row.
const TABLE_SEP = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

function renderInline(text: string, key: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;

  for (const m of text.matchAll(INLINE)) {
    const at = m.index;
    if (at > last) nodes.push(text.slice(last, at));
    last = at + m[0].length;

    if (m[1]) {
      nodes.push(<strong key={`${key}-${at}`}>{m[1]}</strong>);
      continue;
    }

    // Same box as the products page renders these photos at.
    if (m[3] !== undefined) {
      const n = /img([1-4])\.png$/.exec(m[3])?.[1];
      if (n)
        nodes.push(
          <Image
            key={`${key}-${at}`}
            src={`/assets/thailand-premium-tapioca-starch-products/our-products/img${n}.png`}
            alt={m[2]}
            width={330}
            height={359}
          />,
        );
      continue;
    }

    // A bare URL swallows the sentence's closing punctuation — give it back
    // rather than putting the full stop inside the link.
    const raw = m[6];
    const href = m[5] ?? raw.replace(/[.,;:!?)]+$/, "");
    nodes.push(
      <a
        key={`${key}-${at}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {m[4] ?? href}
      </a>,
    );
    if (raw && raw !== href) nodes.push(raw.slice(href.length));
  }

  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function renderRich(text: string): ReactNode {
  const blocks: ReactNode[] = [];
  let bullets: string[] = [];
  let rows: string[] = [];

  const flushBullets = () => {
    if (!bullets.length) return;
    const items = bullets;
    const key = `ul${blocks.length}`;
    bullets = [];
    blocks.push(
      <ul key={key}>
        {items.map((li, i) => (
          <li key={i}>{renderInline(li, `${key}-${i}`)}</li>
        ))}
      </ul>,
    );
  };

  const flushTable = () => {
    if (!rows.length) return;
    const cells = (row: string) =>
      row.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
    const head = rows.length > 1 && TABLE_SEP.test(rows[1]) ? cells(rows[0]) : null;
    const body = rows.slice(head ? 2 : 0).filter((r) => !TABLE_SEP.test(r)).map(cells);
    const key = `t${blocks.length}`;
    rows = [];
    blocks.push(
      <table key={key}>
        {head && (
          <thead>
            <tr>
              {head.map((c, i) => (
                <th key={i}>{renderInline(c, `${key}-h${i}`)}</th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {body.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j}>{renderInline(c, `${key}-${i}-${j}`)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>,
    );
  };

  for (const line of text.split("\n")) {
    // Once a table is open, also keep a pipe-less "---|---" separator and a
    // last row the model cut off before its closing pipe.
    if (
      /^\s*\|.*\|\s*$/.test(line) ||
      (rows.length && (/^\s*\|/.test(line) || TABLE_SEP.test(line)))
    ) {
      flushBullets();
      rows.push(line);
      continue;
    }
    flushTable();
    const bullet = /^\s*[-*]\s+(.+)$/.exec(line);
    if (bullet) {
      bullets.push(bullet[1]);
      continue;
    }
    flushBullets();
    // A stray "## " would otherwise show up verbatim; the prompt body is
    // markdown-shaped, so the model does reach for headings occasionally.
    const body = line.replace(/^\s*#{1,6}\s+/, "").trim();
    if (!body) continue;
    const key = `p${blocks.length}`;
    blocks.push(<p key={key}>{renderInline(body, key)}</p>);
  }

  flushBullets();
  flushTable();
  return blocks;
}

// 24px stroke icons, drawn here rather than pulled from an icon package: three
// glyphs is less code than a dependency and they inherit currentColor.
const ICON = {
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  fill: "none",
} as const;

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden {...ICON}>
      <path d="M20 12a8 8 0 0 1-11.6 7.1L4 20l1-4.2A8 8 0 1 1 20 12Z" />
    </svg>
  );
}

function CloseIcon({ size = 24 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden {...ICON}>
      <path d="m6 6 12 12M18 6 6 18" />
    </svg>
  );
}

function ClearIcon({ size = 18 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden {...ICON}>
      <path d="M4 7h16M10 7V5h4v2M6 7l1 12h10l1-12M10 11v5M14 11v5" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden {...ICON}>
      <path d="M4 12h15M13 6l6 6-6 6" />
    </svg>
  );
}

// .cms-edit-toolbar sits at exactly right:1rem/bottom:1rem with z-index 9999,
// so in CMS edit mode the launcher would be buried under it. Bow out instead.
// Same trigger as EditPageInlineClient. Read through useSyncExternalStore so
// the server snapshot is a plain `false` — no setState-in-effect, no
// hydration mismatch — and not useSearchParams(), which would force a Suspense
// boundary in the layout and opt every route out of static rendering.
const subscribeNever = () => () => {};
const readEditMode = () => {
  const params = new URLSearchParams(window.location.search);
  return (
    params.get("__editPage") === "1" || params.get("__editmode") === "1"
  );
};

// Restored during the useState initializer rather than in an effect. Safe from
// hydration mismatch because the transcript only ever renders inside the panel,
// which is unmounted while closed — server and first client render both emit
// just the launcher.
function restoreTranscript(key: string): ChatMsg[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed: unknown = JSON.parse(
      window.localStorage.getItem(key) || "null",
    );
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (m: unknown): m is ChatMsg =>
          !!m &&
          typeof m === "object" &&
          ((m as ChatMsg).role === "user" || (m as ChatMsg).role === "bot") &&
          typeof (m as ChatMsg).text === "string",
      )
      // Storage is as untrusted as the network here — a tampered entry must
      // not put a `javascript:` href into the transcript.
      .map((m) => ({ ...m, sources: safeSources(m.sources) }));
  } catch {
    // corrupt value, or storage unavailable — start empty
    return [];
  }
}

// Read in the useState initializer for the same reason restoreTranscript is:
// the gate only ever renders inside the panel, which is unmounted while closed,
// so the server and the first client render both emit just the launcher.
type Gate = "consent" | "form" | null;
function restoreGate(): Gate {
  if (typeof window === "undefined") return "consent";
  try {
    return window.localStorage.getItem(GATE_KEY) === "1" ? null : "consent";
  } catch {
    // storage unavailable — ask again rather than let anyone through
    return "consent";
  }
}

export default function Chatbot() {
  const t = useTranslations("Chatbot");
  // The PDPA sentence and the field labels are the contact form's, read from
  // there so there is one copy of them per locale.
  const tc = useTranslations("Contact.Form");
  const locale = useLocale();

  const editMode = useSyncExternalStore(
    subscribeNever,
    readEditMode,
    () => false,
  );

  // Per locale: switching language mid-session must not stack ZH bubbles on EN ones.
  const storageKey = `ppc_chat_v1_${locale}`;

  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [messages, setMessages] = useState<ChatMsg[]>(() =>
    restoreTranscript(storageKey),
  );
  const [chips, setChips] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [justSent, setJustSent] = useState(false);
  const [vvHeight, setVvHeight] = useState<number | null>(null);

  const [gate, setGate] = useState<Gate>(restoreGate);
  const [lead, setLead] = useState({
    first_name: "",
    last_name: "",
    email: "",
    company: "",
    ppc_hp: "", // honeypot
  });
  const [leadBusy, setLeadBusy] = useState(false);
  const [leadError, setLeadError] = useState(false);
  // Loop breaker. A 403 after a successful submit means the cookie cannot land
  // (blocked cookies, a Secure cookie on an http origin, a stripped Set-Cookie,
  // or the honeypot's rowless {ok:true}). Reopening the gate then would bounce
  // the visitor forever and write a row per lap. A ref, not state: `ask` reads
  // it from a closure that must not go stale.
  const leadDoneRef = useRef(false);

  const atLimit = draft.length >= MAX_INPUT;

  const fabRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<ChatMsg[]>(messages);
  const greetedRef = useRef(messages.length > 0);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (!messages.length) return;
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify(messages.slice(-MAX_MESSAGES)),
      );
    } catch {
      // Safari private mode throws on setItem — the transcript is not worth a crash
    }
  }, [messages, storageKey]);

  const ask = useCallback(
    async (text: string) => {
      setBusy(true);
      setChips([]);
      const before = messagesRef.current;
      const history: ChatMsg[] = text
        ? [...before, { role: "user", text }]
        : before;
      if (text) setMessages(history);
      try {
        const reply = await getChatReply(history, t, locale);
        setMessages([
          ...history,
          { role: "bot", text: reply.text, sources: reply.sources },
        ]);
        setChips(reply.chips ?? []);
      } catch (err) {
        // No consent cookie: the flag lied (cleared cookies, or it predates the
        // cookie entirely). Reopen the gate and drop this turn — an error bubble
        // would read as a crash when nothing crashed.
        if (err instanceof Error && err.message === CONSENT_REQUIRED) {
          messagesRef.current = before;
          setDraft(text); // their question, back in the box — the turn is dropped
          // Second time: the form is not the problem, so say so instead of
          // asking again. Same copy the form uses when its own POST fails.
          if (leadDoneRef.current) {
            setMessages([...before, { role: "bot", text: t("gate.error") }]);
            return;
          }
          setMessages(before);
          try {
            window.localStorage.removeItem(GATE_KEY);
          } catch {
            // storage unavailable — the gate below is what actually matters
          }
          setGate("consent");
          return;
        }
        setMessages([...history, { role: "bot", text: t("error") }]);
        setChips([t("retry")]);
      } finally {
        setBusy(false);
      }
    },
    [t, locale],
  );

  // Wipe the transcript, the stored copy and the chips, then greet again so the
  // panel is never left blank. The persist effect skips empty transcripts, so
  // the stored copy has to be removed here.
  const clear = useCallback(() => {
    setMessages([]);
    messagesRef.current = [];
    setChips([]);
    setDraft("");
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      // storage unavailable — the in-memory transcript is already cleared
    }
    inputRef.current?.focus();
    void ask("");
  }, [ask, storageKey]);

  const close = useCallback(() => {
    setOpen(false);
    setClosing(true);
    fabRef.current?.focus();
  }, []);

  // Prefix only when the URL in the address bar already carries one. proxy.ts
  // 308s /{domain's own locale}/* back to the bare path, so a flat
  // `/${locale}/...` would make next/link prefetch a redirect on cn.
  const pathname = usePathname();
  const localized =
    pathname === `/${locale}` || pathname.startsWith(`/${locale}/`);
  const privacyHref = localized ? `/${locale}/privacy-policy` : "/privacy-policy";

  const setField =
    (key: keyof typeof lead) => (e: ChangeEvent<HTMLInputElement>) =>
      setLead((v) => ({ ...v, [key]: e.target.value }));

  const submitLead = async (e: FormEvent) => {
    e.preventDefault();
    if (leadBusy) return;
    const payload = {
      first_name: lead.first_name.trim(),
      last_name: lead.last_name.trim(),
      email: lead.email.trim(),
      company: lead.company.trim(),
      ppc_hp: lead.ppc_hp,
      consent: true,
      locale,
    };

    setLeadBusy(true);
    setLeadError(false);
    try {
      const res = await fetch("/api/chat/lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data: unknown = await res.json().catch(() => null);
      if (!res.ok || !(data as { ok?: boolean } | null)?.ok) {
        throw new Error("lead");
      }
      try {
        window.localStorage.setItem(GATE_KEY, "1");
      } catch {
        // storage unavailable — the gate reopens next visit, the row is saved
      }
      leadDoneRef.current = true;
      setGate(null); // the greeting effect fires now
    } catch {
      setLeadError(true);
    } finally {
      setLeadBusy(false);
    }
  };

  // The panel slides out before it leaves the DOM; the transcript still never
  // renders on the server, so the hydration guarantee above holds.
  useEffect(() => {
    if (!closing) return;
    const id = setTimeout(() => setClosing(false), CLOSE_MS);
    return () => clearTimeout(id);
  }, [closing]);

  // Drives --chat-dock, which squeezes body/navbar/video in the stylesheet.
  useEffect(() => {
    const el = document.documentElement;
    if (open) el.dataset.chatOpen = "1";
    else delete el.dataset.chatOpen;
    return () => {
      delete el.dataset.chatOpen;
    };
  }, [open]);

  // Gated: no composer to focus and nothing to greet yet — the greeting waits
  // until the gate is passed, which re-runs this effect.
  useEffect(() => {
    if (!open || gate) return;
    inputRef.current?.focus();
    if (!greetedRef.current && messagesRef.current.length === 0) {
      greetedRef.current = true;
      void ask("");
    }
  }, [open, gate, ask]);

  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy, open]);

  // Silent success, but not invisible: the composer settles back to its resting
  // border after a send instead of firing a toast.
  useEffect(() => {
    if (!justSent) return;
    const id = setTimeout(() => setJustSent(false), 400);
    return () => clearTimeout(id);
  }, [justSent]);

  // The on-screen keyboard shrinks the visual viewport while a fixed panel
  // keeps its layout height, hiding the input. Track the real height on xs.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!open || !vv) return;
    const sync = () => setVvHeight(vv.height);
    sync();
    vv.addEventListener("resize", sync);
    return () => {
      vv.removeEventListener("resize", sync);
      setVvHeight(null);
    };
  }, [open]);

  if (editMode) return null;

  return (
    <div className={s.root}>
      <button
        ref={fabRef}
        type="button"
        className={s.launcher}
        onClick={() => (open ? close() : setOpen(true))}
        aria-label={t(open ? "close" : "launch")}
        aria-expanded={open}
        aria-controls="chatbot-panel"
      >
        {open ? <CloseIcon /> : <ChatIcon />}
      </button>

      {(open || closing) && (
        <section
          id="chatbot-panel"
          role="dialog"
          aria-label={t("title")}
          className={`${s.panel}${closing ? ` ${s.panelOut}` : ""}`}
          style={
            vvHeight ? ({ "--chat-vh": `${vvHeight}px` } as CSSProperties) : undefined
          }
          onKeyDown={(e) => {
            if (e.key === "Escape") close();
          }}
        >
          <header className={s.head}>
            <span className={s.mark} aria-hidden />
            <div className={s.headText}>
              <p className={s.title}>{t("title")}</p>
              <p className={s.subtitle}>{t("subtitle")}</p>
            </div>
            <button
              type="button"
              className={s.iconBtn}
              onClick={clear}
              disabled={busy || !!gate || messages.length === 0}
              aria-label={t("clear")}
              title={t("clear")}
            >
              <ClearIcon />
            </button>
            <button
              type="button"
              className={s.iconBtn}
              onClick={close}
              aria-label={t("close")}
            >
              <CloseIcon size={18} />
            </button>
          </header>

          {gate === "consent" ? (
            <div className={s.gate}>
              <h3 className={s.gateTitle}>{t("gate.consentTitle")}</h3>
              <p className={s.gateBody}>{tc("terms")}</p>
              <p className={s.gateBody}>
                <Link className={s.gateLink} href={privacyHref}>
                  {t("gate.privacyLink")}
                </Link>
              </p>
              <div className={s.gateActions}>
                <button
                  type="button"
                  className={s.gateTile}
                  onClick={() => setGate("form")}
                  autoFocus
                >
                  {t("gate.accept")}
                </button>
                <button type="button" className={s.gateTileQuiet} onClick={close}>
                  {t("gate.decline")}
                </button>
              </div>
            </div>
          ) : gate === "form" ? (
            <form
              className={s.gate}
              onSubmit={submitLead}
              aria-busy={leadBusy}
            >
              <h3 className={s.gateTitle}>{t("gate.formTitle")}</h3>
              <p className={s.gateBody}>{t("gate.formSubtitle")}</p>

              <div className={s.gateRow}>
                <label className={s.gateField}>
                  <span className={s.gateLabel}>{tc("first_name")}</span>
                  <input
                    className={s.gateInput}
                    value={lead.first_name}
                    onChange={setField("first_name")}
                    disabled={leadBusy}
                    autoComplete="given-name"
                    autoFocus
                    required
                  />
                </label>
                <label className={s.gateField}>
                  <span className={s.gateLabel}>{tc("last_name")}</span>
                  <input
                    className={s.gateInput}
                    value={lead.last_name}
                    onChange={setField("last_name")}
                    disabled={leadBusy}
                    autoComplete="family-name"
                    required
                  />
                </label>
              </div>

              <label className={s.gateField}>
                <span className={s.gateLabel}>{tc("email")}</span>
                <input
                  className={s.gateInput}
                  type="email"
                  pattern="[^\s@]+@[^\s@]+\.[^\s@]{2,}"
                  value={lead.email}
                  onChange={setField("email")}
                  disabled={leadBusy}
                  autoComplete="email"
                  required
                />
              </label>

              <label className={s.gateField}>
                <span className={s.gateLabel}>{tc("company")}</span>
                <input
                  className={s.gateInput}
                  value={lead.company}
                  onChange={setField("company")}
                  disabled={leadBusy}
                  autoComplete="organization"
                />
              </label>

              {/* Honeypot — off-screen via the same .sr the typing label uses.
                  Name and autoComplete token are deliberately meaningless: Edge
                  autofill fills name="website" and ignores autocomplete="off". */}
              <input
                className={s.sr}
                name="ppc_hp"
                value={lead.ppc_hp}
                onChange={setField("ppc_hp")}
                tabIndex={-1}
                autoComplete="ppc-hp-nofill"
                aria-hidden
              />

              {leadError && (
                <p className={s.gateError} role="alert">
                  {t("gate.error")}
                </p>
              )}

              <div className={s.gateActions}>
                <button
                  type="submit"
                  className={s.gatePrimary}
                  disabled={leadBusy}
                >
                  {leadBusy ? t("gate.submitting") : t("gate.submit")}
                </button>
              </div>
            </form>
          ) : (
            <>
          <div
            ref={logRef}
            className={s.log}
            role="log"
            aria-live="polite"
            aria-relevant="additions"
          >
            {messages.map((m, i) =>
              m.role === "bot" ? (
                <div key={i} className={s.bot}>
                  {renderRich(m.text)}
                  {m.sources?.length ? (
                    <div className={s.sources}>
                      <span className={s.sourcesLabel}>{t("sources")}</span>
                      {m.sources.map((src) => (
                        <a
                          key={src.url}
                          className={s.source}
                          href={src.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {src.title}
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div key={i} className={s.user}>
                  {m.text}
                </div>
              ),
            )}

            {busy && (
              <div className={s.typing} role="status">
                <span className={s.sr}>{t("typing")}</span>
                <span className={s.dot} aria-hidden />
                <span className={s.dot} aria-hidden />
                <span className={s.dot} aria-hidden />
              </div>
            )}
          </div>

          {chips.length > 0 && (
            <div className={s.chips} role="group" aria-label={t("suggestions")}>
              {chips.map((label) => (
                <button
                  key={label}
                  type="button"
                  className={s.chip}
                  disabled={busy}
                  onClick={() => void ask(label === t("retry") ? "" : label)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          <form
            className={s.composer}
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              const value = draft.trim();
              if (!value || busy) return;
              setDraft("");
              setJustSent(true);
              void ask(value);
            }}
          >
            <div
              className={s.pill}
              aria-busy={busy}
              data-state={
                busy
                  ? "loading"
                  : atLimit
                    ? "error"
                    : justSent
                      ? "sent"
                      : "idle"
              }
            >
              <input
                ref={inputRef}
                className={s.field}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t("placeholder")}
                aria-label={t("placeholder")}
                maxLength={MAX_INPUT}
              />

              {draft.length >= MAX_INPUT - 100 && (
                <span
                  aria-hidden
                  className={`${s.count}${atLimit ? ` ${s.countOver}` : ""}`}
                >
                  {draft.length}/{MAX_INPUT}
                </span>
              )}

              <button
                type="submit"
                className={s.send}
                aria-label={t("send")}
                disabled={!draft.trim() || busy}
              >
                <SendIcon />
              </button>
            </div>
          </form>
            </>
          )}
        </section>
      )}
    </div>
  );
}
